import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../../constants";
import { cx, useUi } from "../../ui";
import FieldRow from "../../components/FieldRow";
import { SHIFT_TIME_OPTIONS } from "../../shifts/constants";
import { formatTimeOfDay } from "../../shifts/shiftHelpers";
import {
  DEFAULT_BLACKOUT_PERIOD,
  dateBound,
  endDateBound,
  localDate,
  validateBlackout,
} from "../../blackouts/blackoutHelpers";

const useStyles = makeStyles({
  card: { border: `1px solid ${T.borderSoft}`, borderRadius: 8, padding: "10px 12px 2px", marginBottom: 8 },
  inactive: { opacity: 0.6 },
  head: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 13, marginBottom: 8 },
  name: { fontWeight: 600, color: T.fg },
  chip: { borderRadius: 999, border: `1px solid ${T.border}`, padding: "1px 8px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: T.mutedFg },
  summary: { marginLeft: "auto", fontSize: 12, color: T.mutedFg },
  grid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "0 14px" },
  full: { gridColumn: "1 / -1" },
  check: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginTop: 6 },
  error: { fontSize: 12, color: T.danger },
  actions: { display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap", margin: "10px 0" },
  confirmPrompt: { marginRight: "auto", alignSelf: "center", fontSize: 13, color: T.danger },
  small: { padding: "6px 12px", fontSize: 13 },
  danger: { borderRadius: 6, border: `1px solid ${T.danger}`, background: T.danger, color: T.dangerFg, fontSize: 13, fontFamily: "inherit", padding: "6px 12px", cursor: "pointer", "&:disabled": { opacity: 0.4, cursor: "not-allowed" } },
});

function toDraft(period, timezone) {
  return {
    id: period.id ?? null,
    name: period.name ?? "",
    reason: period.reason ?? "",
    startDate: localDate(period.startsAt, timezone),
    endDate: localDate(period.endsAt, timezone),
    startTime: period.startTime ?? DEFAULT_BLACKOUT_PERIOD.startTime,
    endTime: period.endTime ?? DEFAULT_BLACKOUT_PERIOD.endTime,
    isActive: period.isActive ?? true,
  };
}

export default function BlackoutPeriodRow({ period, timezone, disabled, onSave, onDelete, onCancel }) {
  const s = useStyles();
  const ui = useUi();
  const original = useMemo(() => toDraft(period, timezone), [period, timezone]);
  const [draft, setDraft] = useState(original);
  const [submitting, setSubmitting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  useEffect(() => setDraft(original), [original]);

  const setField = (field) => (event) => {
    const value = event.target.value;
    setDraft((current) => ({ ...current, [field]: value }));
  };
  const setMinutes = (field) => (event) => {
    const value = parseInt(event.target.value, 10);
    setDraft((current) => ({ ...current, [field]: value }));
  };
  const toggleActive = (event) => {
    const { checked } = event.target;
    setDraft((current) => ({ ...current, isActive: checked }));
  };

  const isNew = !original.id;
  const errors = validateBlackout(draft);
  const firstError = Object.values(errors)[0];
  const isDirty = JSON.stringify(draft) !== JSON.stringify(original);
  const isBlocked = disabled || submitting;
  const canSave = !firstError && (isNew || isDirty) && !isBlocked;
  const id = original.id ?? "new";
  const overnight = draft.endTime <= draft.startTime;

  const save = async (event) => {
    event.preventDefault();
    if (!canSave) return;
    setSubmitting(true);
    try {
      await onSave({
        ...draft,
        startsAt: dateBound(draft.startDate, timezone),
        endsAt: endDateBound(draft.startDate, draft.endDate, timezone),
      });
    } finally {
      setSubmitting(false);
    }
  };
  const remove = async () => {
    if (isBlocked) return;
    setSubmitting(true);
    try { await onDelete(original); }
    finally { setSubmitting(false); setConfirmingDelete(false); }
  };

  return (
    <li className={cx(s.card, !draft.isActive && s.inactive)}>
      <form onSubmit={save}>
        <div className={s.head}>
          <span className={s.name}>{draft.name || "New blackout"}</span>
          {overnight && <span className={s.chip}>Overnight</span>}
          {!draft.isActive && <span className={s.chip}>Disabled</span>}
          <span className={s.summary}>{formatTimeOfDay(draft.startTime)} &ndash; {formatTimeOfDay(draft.endTime)}</span>
        </div>
        <div className={s.grid}>
          <FieldRow label="Name" htmlFor={`bo-name-${id}`}>
            <input id={`bo-name-${id}`} className={ui.field} disabled={isBlocked} value={draft.name} onChange={setField("name")} />
            {errors.name && <span className={s.error}>{errors.name}</span>}
          </FieldRow>
          <FieldRow label="Reason" htmlFor={`bo-reason-${id}`}>
            <input id={`bo-reason-${id}`} className={ui.field} disabled={isBlocked} value={draft.reason} onChange={setField("reason")} />
          </FieldRow>
          <FieldRow label="Start date" htmlFor={`bo-start-date-${id}`}>
            <input id={`bo-start-date-${id}`} type="date" className={ui.field} disabled={isBlocked} value={draft.startDate} onChange={setField("startDate")} />
          </FieldRow>
          <FieldRow label="End date" htmlFor={`bo-end-date-${id}`}>
            <input id={`bo-end-date-${id}`} type="date" className={ui.field} disabled={isBlocked} value={draft.endDate} min={draft.startDate || undefined} onChange={setField("endDate")} />
            {errors.endDate && <span className={s.error}>{errors.endDate}</span>}
          </FieldRow>
          <FieldRow label="Blackout starts" htmlFor={`bo-start-${id}`}>
            <select id={`bo-start-${id}`} className={ui.field} disabled={isBlocked} value={draft.startTime} onChange={setMinutes("startTime")}>
              {SHIFT_TIME_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </FieldRow>
          <FieldRow label="Blackout ends" htmlFor={`bo-end-${id}`}>
            <select id={`bo-end-${id}`} className={ui.field} disabled={isBlocked} value={draft.endTime} onChange={setMinutes("endTime")}>
              {SHIFT_TIME_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            {errors.endTime && <span className={s.error}>{errors.endTime}</span>}
          </FieldRow>
        </div>
        <label className={s.check}>
          <input type="checkbox" disabled={isBlocked} checked={draft.isActive} onChange={toggleActive} />
          This blackout period is active
        </label>
        <div className={s.actions}>
          {confirmingDelete ? <>
            <span className={s.confirmPrompt}>Delete this blackout period?</span>
            <button type="button" className={cx(ui.mutedBtn, s.small)} disabled={isBlocked} onClick={() => setConfirmingDelete(false)}>Keep</button>
            <button type="button" className={s.danger} disabled={isBlocked} onClick={remove}>Delete</button>
          </> : <>
            {isNew ? <button type="button" className={cx(ui.mutedBtn, s.small)} onClick={onCancel}>Cancel</button> : <>
              <button type="button" className={cx(ui.mutedBtn, s.small)} disabled={isBlocked} onClick={() => setConfirmingDelete(true)}>Delete</button>
              <button type="button" className={cx(ui.mutedBtn, s.small)} disabled={!isDirty || isBlocked} onClick={() => setDraft(original)}>Cancel</button>
            </>}
            <button type="submit" className={cx(ui.primaryBtn, s.small)} disabled={!canSave}>{isNew ? "Add period" : "Save"}</button>
          </>}
        </div>
      </form>
    </li>
  );
}
